import { Navigate, Outlet } from 'react-router';
import { useAuthClient } from '@/lib/authClient';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useDeviceConnection } from '@/hooks/use-device-connection';
import { useAccounts } from '@/hooks/account/useAccounts';
import { setOrgCookie } from '@/lib/api/orgCookie';
import { ORG_COOKIE_NAME } from '@mls-wallet/shared-types';

function getCookieValue(name: string): string | null {
  const cookies = document.cookie.split(';').map((c) => c.trim());
  const prefix = `${name}=`;
  for (const cookie of cookies) {
    if (cookie.startsWith(prefix)) return cookie.slice(prefix.length) || null;
  }
  return null;
}

/**
 * Auth guard that requires:
 * 1. User is signed in
 * 2. User has org membership
 * 3. Organization has at least one account
 *
 * If no accounts exist, redirects admin to /create-first-account
 */
export function RequireOrgHasAccounts() {
  const { session } = useAuthClient();
  const { isChecking, isConnected } = useDeviceConnection();
  const { organization, isLoading: isOrgLoading } = useMyOrganization({
    // Avoid calling /organization/my when mobile is disconnected.
    enabled: isConnected,
  });

  const orgId = organization?.id ?? null;

  // 1) Synchronously ensure org cookie is set BEFORE fetching org-scoped resources.
  // This makes the fetch order effectively sequential: org -> set cookie -> accounts.
  if (orgId) {
    const currentCookieOrgId = getCookieValue(ORG_COOKIE_NAME);
    if (currentCookieOrgId !== orgId) {
      setOrgCookie(orgId);
    }
  }

  // 2) Fetch accounts only after org is known (and cookie is set).
  const accountsEnabled = Boolean(session.data && orgId);
  const {
    accounts,
    error: accountsError,
    isLoading: isAccountsLoading,
  } = useAccounts(undefined, accountsEnabled);

  // Wait for all data to load (mobile, org, accounts)
  if (session.isPending) return null;
  if (isChecking) return null;
  if (isOrgLoading) return null;
  if (session.data && orgId && isAccountsLoading) return null;

  // No session - redirect to signin
  if (!session.data) return <Navigate to="/signin" replace />;

  // No mobile device connected - redirect to register device
  if (!isConnected) return <Navigate to="/register-device" replace />;

  // No org membership - redirect to waiting approval
  if (!organization) return <Navigate to="/waiting-approval" replace />;

  if (accountsError) throw accountsError;

  // Org has no accounts
  if (accounts.length === 0) {
    // Non-admin with no accounts should never happen - only the org creator
    // (who is admin) should exist at this stage
    if (!organization.isAdmin) {
      throw new Error(
        'Invalid state: Non-admin user in organization with no accounts. ' +
          'This should not be possible in the normal flow.'
      );
    }

    return <Navigate to="/create-first-account" replace />;
  }

  // All checks passed - render child routes
  return <Outlet />;
}
